/**
 * AR Exception Report - Query Hooks & Filters
 *
 * Custom hooks for:
 * - Fetching AR data using TanStack React Query
 * - Managing filter state and URL synchronization
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { arReportApi } from '@xtnd/web/features/reports/api/arReportApi';
import type { ArExceptionFlag, ArFiltersState } from '@xtnd/web/features/reports/types';

// ============================================================================
// Types & Interfaces
// ============================================================================

// Re-export types for backwards compatibility
export type { ArExceptionFlag, ArFiltersState } from '@xtnd/web/features/reports/types';

const DEFAULT_FILTERS: ArFiltersState = {
  clients: [],
  flags: [],
};

// ============================================================================
// Filter Utilities
// ============================================================================

/**
 * Parse URL search params into filter state
 */
function parseFiltersFromUrl(searchParams: URLSearchParams): ArFiltersState {
  const filters: ArFiltersState = { ...DEFAULT_FILTERS };

  // Parse array parameters
  const clients = searchParams.getAll('clients');
  if (clients.length > 0) {
    filters.clients = clients;
  }

  const flags = searchParams.getAll('flags') as ArExceptionFlag[];
  if (flags.length > 0) {
    filters.flags = flags;
  }

  // Parse pagination parameters
  const page = searchParams.get('page');
  if (page) {
    filters.page = parseInt(page, 10);
  }

  const limit = searchParams.get('limit');
  if (limit) {
    filters.limit = parseInt(limit, 10);
  }

  return filters;
}


// ============================================================================
// Query Keys
// ============================================================================

// Query keys for caching
export const AR_QUERY_KEYS = {
  exceptions: (filters: any) => ['ar', 'exceptions', filters] as const,
  pivot: (filters: any) => ['ar', 'pivot', filters] as const,
  summary: () => ['ar', 'summary'] as const,
  clientBreakdown: (clientName: string, options: any) => ['ar', 'client-breakdown', clientName, options] as const,
  export: (params: any) => ['ar', 'export', params] as const,
  health: () => ['ar', 'health'] as const,
};

/**
 * Hook for fetching invoice exceptions with pagination (only clientIds filter supported)
 */
export function useArExceptions(
  filters: ArFiltersState & { page?: number; limit?: number },
  options?: { enabled?: boolean }
) {
  // Extract clientIds, flags filter and pagination for exceptions
  // For exceptions endpoint, filters.clients should contain client IDs
  const exceptionParams = {
    clientIds: filters.clients, // clients array should contain client IDs for exceptions
    flags: filters.flags, // status flags for filtering
    page: filters.page || 1,
    limit: filters.limit || 100,
  };

  return useQuery({
    queryKey: AR_QUERY_KEYS.exceptions(exceptionParams),
    queryFn: () => arReportApi.getInvoiceExceptions(exceptionParams),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

/**
 * Hook for fetching client pivot data (only clientIds filter supported)
 */
export function useArPivot(
  filters: ArFiltersState & { page?: number; limit?: number },
  options?: { enabled?: boolean }
) {
  // For pivot endpoint, filters.clients should contain clientIds
  const pivotParams = {
    clientIds: filters.clients, // clients array should contain clientIds for pivot
    page: filters.page || 1,
    limit: filters.limit || 100,
  };

  return useQuery({
    queryKey: AR_QUERY_KEYS.pivot(pivotParams),
    queryFn: () => arReportApi.getClientPivot(pivotParams),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook for fetching summary statistics (all data, no filters)
 */
export function useArSummaryStats() {
  return useQuery({
    queryKey: AR_QUERY_KEYS.summary(),
    queryFn: () => arReportApi.getSummaryStats(),
    staleTime: 5 * 60 * 1000, // 5 minutes (summary changes less frequently)
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}


/**
 * Hook for fetching detailed client breakdown
 */
export function useArClientBreakdown(
  clientName: string,
  options: {
    excludeCurrencyMismatch?: boolean;
    sortBy?: 'variance' | 'clioOpen' | 'qboOpen' | 'invoiceNumber';
    sortDirection?: 'asc' | 'desc';
  } = {},
  enabled = true
) {
  return useQuery({
    queryKey: AR_QUERY_KEYS.clientBreakdown(clientName, options),
    queryFn: () => arReportApi.getClientBreakdown(clientName, options),
    enabled: enabled && Boolean(clientName),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook for AR service health check
 */
export function useArHealthCheck() {
  return useQuery({
    queryKey: AR_QUERY_KEYS.health(),
    queryFn: () => arReportApi.getHealthCheck(),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

/**
 * Hook for fetching available clients from clio-contacts
 */
export function useArAvailableClients() {
  return useQuery({
    queryKey: ['ar', 'clients'],
    queryFn: () => arReportApi.getAvailableClients(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 2,
  });
}

/**
 * Hook for exporting AR data
 */
export function useArExport() {
  return useMutation({
    mutationFn: (params: {
      type: 'csv' | 'xls' | 'pdf';
      scope: 'invoices' | 'pivot';
      filters: ArFiltersState;
      includeMetadata?: boolean;
    }) => arReportApi.exportData(params),
    onSuccess: () => {
      // Optionally show success notification
      console.log('Export completed successfully');
    },
    onError: (error) => {
      // Handle export errors
      console.error('Export failed:', error);
    },
  });
}

/**
 * Hook for invalidating and refetching AR data
 */
export function useArDataRefresh() {
  const queryClient = useQueryClient();

  const refreshAll = () => {
    // Invalidate all AR-related queries
    queryClient.invalidateQueries({ queryKey: ['ar'] });
  };

  const refreshExceptions = (filters?: any) => {
    if (filters) {
      queryClient.invalidateQueries({ queryKey: AR_QUERY_KEYS.exceptions(filters) });
    } else {
      queryClient.invalidateQueries({ queryKey: ['ar', 'exceptions'] });
    }
  };

  const refreshPivot = (filters?: any) => {
    if (filters) {
      queryClient.invalidateQueries({ queryKey: AR_QUERY_KEYS.pivot(filters) });
    } else {
      queryClient.invalidateQueries({ queryKey: ['ar', 'pivot'] });
    }
  };

  const refreshSummary = () => {
    queryClient.invalidateQueries({ queryKey: AR_QUERY_KEYS.summary() });
  };

  return {
    refreshAll,
    refreshExceptions,
    refreshPivot,
    refreshSummary,
  };
}

/**
 * Hook for prefetching related data
 */
export function useArPrefetch() {
  const queryClient = useQueryClient();

  const prefetchClientBreakdown = (
    clientName: string,
    options: {
      excludeCurrencyMismatch?: boolean;
      sortBy?: 'variance' | 'clioOpen' | 'qboOpen' | 'invoiceNumber';
      sortDirection?: 'asc' | 'desc';
    } = {}
  ) => {
    queryClient.prefetchQuery({
      queryKey: AR_QUERY_KEYS.clientBreakdown(clientName, options),
      queryFn: () => arReportApi.getClientBreakdown(clientName, options),
      staleTime: 2 * 60 * 1000,
    });
  };

  const prefetchExceptions = (filters: ArFiltersState & { page?: number; limit?: number }) => {
    queryClient.prefetchQuery({
      queryKey: AR_QUERY_KEYS.exceptions(filters),
      queryFn: () => arReportApi.getInvoiceExceptions(filters),
      staleTime: 2 * 60 * 1000,
    });
  };

  return {
    prefetchClientBreakdown,
    prefetchExceptions,
  };
}

/**
 * Hook for optimistic updates (for future use with data modifications)
 */
export function useArOptimisticUpdates() {
  const queryClient = useQueryClient();

  const updateExceptionOptimistically = (
    filters: any,
    updater: (old: any) => any
  ) => {
    queryClient.setQueryData(AR_QUERY_KEYS.exceptions(filters), updater);
  };

  const updatePivotOptimistically = (
    filters: any,
    updater: (old: any) => any
  ) => {
    queryClient.setQueryData(AR_QUERY_KEYS.pivot(filters), updater);
  };

  return {
    updateExceptionOptimistically,
    updatePivotOptimistically,
  };
}

/**
 * Progressive loading hook - shows data as each endpoint completes
 * Displays data immediately when available instead of waiting for all endpoints
 */
export function useArDataProgressive(
  activeTab?: 'pivot' | 'invoices',
  options?: {
    pivotFilters?: ArFiltersState;
    invoiceFilters?: ArFiltersState;
    pivotPagination?: { page: number; limit: number };
    invoicePagination?: { page: number; limit: number };
  }
) {
  // Always fetch summary data (no filters)
  const summaryQuery = useArSummaryStats();

  // Conditionally fetch pivot data only when needed
  const pivotQuery = useArPivot({
    clients: options?.pivotFilters?.clients || [],
    flags: options?.pivotFilters?.flags || [],
    page: options?.pivotPagination?.page || 1,
    limit: options?.pivotPagination?.limit || 100,
  }, {
    enabled: !activeTab || activeTab === 'pivot',
  });

  // Conditionally fetch exceptions data only when needed
  const exceptionsQuery = useArExceptions({
    clients: options?.invoiceFilters?.clients || [],
    flags: options?.invoiceFilters?.flags || [],
    page: options?.invoicePagination?.page || 1,
    limit: options?.invoicePagination?.limit || 100,
  }, {
    enabled: !activeTab || activeTab === 'invoices',
  });

  // Individual loading states for progressive display
  const loadingStates = {
    summary: summaryQuery.isLoading,
    pivot: (!activeTab || activeTab === 'pivot') ? pivotQuery.isLoading : false,
    exceptions: (!activeTab || activeTab === 'invoices') ? exceptionsQuery.isLoading : false,
  };

  // Individual error states
  const errorStates = {
    summary: summaryQuery.error,
    pivot: (!activeTab || activeTab === 'pivot') ? pivotQuery.error : null,
    exceptions: (!activeTab || activeTab === 'invoices') ? exceptionsQuery.error : null,
  };

  // Data availability flags
  const dataAvailable = {
    summary: !!summaryQuery.data && !summaryQuery.isLoading,
    pivot: !!pivotQuery.data && !pivotQuery.isLoading,
    exceptions: !!exceptionsQuery.data && !exceptionsQuery.isLoading,
  };

  // Overall loading state (true only if ALL enabled queries are loading)
  const isAllLoading = summaryQuery.isLoading &&
    ((!activeTab || activeTab === 'pivot') ? pivotQuery.isLoading : true) &&
    ((!activeTab || activeTab === 'invoices') ? exceptionsQuery.isLoading : true);

  // Any loading state (true if ANY enabled query is loading)
  const isAnyLoading = summaryQuery.isLoading ||
    ((!activeTab || activeTab === 'pivot') && pivotQuery.isLoading) ||
    ((!activeTab || activeTab === 'invoices') && exceptionsQuery.isLoading);

  // First error encountered
  const firstError = summaryQuery.error ||
    ((!activeTab || activeTab === 'pivot') && pivotQuery.error) ||
    ((!activeTab || activeTab === 'invoices') && exceptionsQuery.error);

  // Refetch all data
  const refetch = () => {
    summaryQuery.refetch();
    if (!activeTab || activeTab === 'pivot') {
      pivotQuery.refetch();
    }
    if (!activeTab || activeTab === 'invoices') {
      exceptionsQuery.refetch();
    }
  };

  return {
    // Data (available immediately when each query completes)
    summaryData: summaryQuery.data,
    pivotData: pivotQuery.data,
    exceptionsData: exceptionsQuery.data,

    // Progressive loading states
    loadingStates,
    errorStates,
    dataAvailable,

    // Overall states
    isAllLoading,
    isAnyLoading,
    firstError,

    // Legacy compatibility
    isLoading: isAllLoading, // For backward compatibility
    error: firstError, // For backward compatibility

    // Actions
    refetch,
  };
}

/**
 * Combined hook for AR Exception Page - fetches all required data
 * @deprecated Use useArDataProgressive for better UX with progressive loading
 */
export function useArData(filters: ArFiltersState, activeTab?: 'pivot' | 'invoices') {
  // Always fetch summary data (no filters)
  const summaryQuery = useArSummaryStats();

  // Conditionally fetch pivot data only when needed
  const pivotQuery = useArPivot(filters, {
    enabled: !activeTab || activeTab === 'pivot', // Only fetch when on pivot tab or no tab specified
  });

  // Conditionally fetch exceptions data only when needed
  const exceptionsQuery = useArExceptions({ ...filters, page: 1, limit: 100 }, {
    enabled: !activeTab || activeTab === 'invoices', // Only fetch when on invoices tab or no tab specified
  });

  // Combined loading state - only consider enabled queries
  const isLoading = summaryQuery.isLoading ||
    ((!activeTab || activeTab === 'pivot') && pivotQuery.isLoading) ||
    ((!activeTab || activeTab === 'invoices') && exceptionsQuery.isLoading);

  // Combined error state - return first error found from enabled queries
  const error = summaryQuery.error ||
    ((!activeTab || activeTab === 'pivot') && pivotQuery.error) ||
    ((!activeTab || activeTab === 'invoices') && exceptionsQuery.error);

  // Refetch all data
  const refetch = () => {
    summaryQuery.refetch();
    if (!activeTab || activeTab === 'pivot') {
      pivotQuery.refetch();
    }
    if (!activeTab || activeTab === 'invoices') {
      exceptionsQuery.refetch();
    }
  };

  return {
    summaryData: summaryQuery.data,
    pivotData: pivotQuery.data,
    exceptionsData: exceptionsQuery.data,
    isLoading,
    error,
    refetch,
  };
}

// ============================================================================
// Filter Management Hooks
// ============================================================================

/**
 * Hook for managing AR filter state with URL synchronization
 * Automatically syncs filter state with URL search params
 */
export function useArFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<ArFiltersState>(() =>
    parseFiltersFromUrl(searchParams)
  );

  // Sync filters with URL when search params change
  useEffect(() => {
    const newFilters = parseFiltersFromUrl(searchParams);
    setFilters(newFilters);
  }, [searchParams]);

  // Update filters and URL
  const updateFilters = useCallback((newFilters: Partial<ArFiltersState>) => {
    const updatedFilters = { ...filters, ...newFilters };

    // Update local state immediately for responsive UI
    setFilters(updatedFilters);

    // Build URL params inline
    const newParams = new URLSearchParams();

    // Add clients
    updatedFilters.clients.forEach(client => {
      newParams.append('clients', client);
    });

    // Add flags
    updatedFilters.flags.forEach(flag => {
      newParams.append('flags', flag);
    });

    // Add pagination
    if (updatedFilters.page) {
      newParams.set('page', updatedFilters.page.toString());
    }

    if (updatedFilters.limit) {
      newParams.set('limit', updatedFilters.limit.toString());
    }

    // Preserve non-filter params (like tab)
    const currentTab = searchParams.get('tab');
    if (currentTab) {
      newParams.set('tab', currentTab);
    }

    setSearchParams(newParams);
  }, [filters, searchParams, setSearchParams]);

  // Reset filters to default
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);

    // Keep only non-filter params
    const newParams = new URLSearchParams();
    const currentTab = searchParams.get('tab');
    if (currentTab) {
      newParams.set('tab', currentTab);
    }

    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Add specific filter utilities
  const addClient = useCallback((client: string) => {
    if (!filters.clients.includes(client)) {
      updateFilters({ clients: [...filters.clients, client] });
    }
  }, [filters.clients, updateFilters]);

  const removeClient = useCallback((client: string) => {
    updateFilters({ clients: filters.clients.filter(c => c !== client) });
  }, [filters.clients, updateFilters]);

  const toggleFlag = useCallback((flag: ArExceptionFlag) => {
    const currentFlags = filters.flags;
    const newFlags = currentFlags.includes(flag)
      ? currentFlags.filter(f => f !== flag)
      : [...currentFlags, flag];
    updateFilters({ flags: newFlags });
  }, [filters.flags, updateFilters]);

  // Check if filters are active (non-default)
  const hasActiveFilters =
    filters.clients.length > 0 ||
    filters.flags.length > 0;

  return {
    filters,
    updateFilters,
    resetFilters,
    addClient,
    removeClient,
    toggleFlag,
    hasActiveFilters,
  };
}
